<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-3xl font-bold text-gray-900">API Keys</h2>
      <button @click="showCreateForm = true" class="btn btn-primary">
        Create API Key
      </button>
    </div>

    <!-- Search Bar -->
    <div class="bg-gray-50 p-4 rounded-lg border">
      <div class="flex items-center space-x-4">
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">🔍 Search API Keys</label>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by API Key ID, Plan, Status, Usage, or any visible information..."
            class="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div class="flex items-end">
          <button
            @click="clearSearch"
            v-if="searchQuery"
            class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-100"
          >
            Clear
          </button>
        </div>
      </div>
      <div class="mt-2 text-xs text-gray-500">
        Showing {{ filteredApiKeys.length }} of {{ apiKeys.length }} API keys
      </div>
    </div>

    <div v-if="loading" class="text-center py-12">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      <p class="mt-2 text-gray-600">Loading API keys...</p>
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
      <div class="flex">
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">Error</h3>
          <div class="mt-2 text-sm text-red-700">{{ error }}</div>
        </div>
      </div>
    </div>

    <div v-else class="card">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires At</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage (Min/Day/Month)</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="apiKey in filteredApiKeys" :key="apiKey.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ apiKey.id }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ apiKey.plan }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(apiKey.expiresAt) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <span v-if="apiKey.usage">
                  {{ apiKey.usage.minute }}/{{ apiKey.usage.day }}/{{ apiKey.usage.month }}
                </span>
                <span v-else>-</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <button @click="editApiKey(apiKey)" class="btn btn-primary btn-sm">
                  Edit
                </button>
                <button @click="deleteApiKey(apiKey.id)" class="btn btn-danger btn-sm">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="apiKeys.length === 0" class="text-center py-12">
          <p class="text-gray-500">No API keys found. Create your first API key to get started.</p>
        </div>
      </div>
    </div>
    
    <!-- Create/Edit Modal -->
    <div v-if="showCreateForm || editingApiKey" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click="closeModal">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" @click.stop>
        <h3 class="text-lg font-bold text-gray-900 mb-4">{{ editingApiKey ? 'Edit API Key' : 'Create API Key' }}</h3>

        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">API Key ID</label>
            <input
              v-model="formData.id"
              type="text"
              required
              :disabled="!!editingApiKey"
              placeholder="Enter unique API key ID"
              class="form-input"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select v-model="formData.plan" required class="form-select">
              <option value="">Select a plan</option>
              <option v-for="plan in plans" :key="plan.name" :value="plan.name">
                {{ plan.name }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
            <input
              v-model="formData.expiresAt"
              type="datetime-local"
              required
              class="form-input"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select v-model="formData.status" required class="form-select">
              <option :value="1">Active</option>
              <option :value="2">Suspended</option>
              <option :value="3">Revoked</option>
            </select>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button type="button" @click="closeModal" class="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              {{ submitting ? 'Saving...' : (editingApiKey ? 'Update' : 'Create') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { adminApi } from '@/services/api'
import type { ApiKey, Plan, CreateApiKey, UpdateApiKey } from '@/types'
import { ApiKeyStatus } from '@/types'

const apiKeys = ref<ApiKey[]>([])
const plans = ref<Plan[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const showCreateForm = ref(false)
const editingApiKey = ref<ApiKey | null>(null)
const submitting = ref(false)

const formData = ref({
  id: '',
  plan: '',
  expiresAt: '',
  status: ApiKeyStatus.Active
})

// Search functionality
const searchQuery = ref('')

const filteredApiKeys = computed(() => {
  if (!searchQuery.value.trim()) {
    return apiKeys.value
  }

  const query = searchQuery.value.toLowerCase().trim()

  return apiKeys.value.filter(apiKey => {
    // Search across all visible fields
    const searchableText = [
      apiKey.id,
      apiKey.plan,
      formatDate(apiKey.expiresAt),
      apiKey.usage ? `${apiKey.usage.minute}/${apiKey.usage.day}/${apiKey.usage.month}` : ''
    ].join(' ').toLowerCase()

    return searchableText.includes(query)
  })
})

const loadApiKeys = async () => {
  loading.value = true
  error.value = null
  
  try {
    apiKeys.value = await adminApi.getApiKeys()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to load API keys'
  } finally {
    loading.value = false
  }
}

const loadPlans = async () => {
  try {
    plans.value = await adminApi.getPlans()
  } catch (err: any) {
    console.error('Failed to load plans:', err)
  }
}



const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString()
}

const editApiKey = (apiKey: ApiKey) => {
  editingApiKey.value = apiKey
  formData.value = {
    id: apiKey.id,
    plan: apiKey.plan,
    expiresAt: new Date(apiKey.expiresAt).toISOString().slice(0, 16),
    status: apiKey.status
  }
}

const closeModal = () => {
  showCreateForm.value = false
  editingApiKey.value = null
  formData.value = {
    id: '',
    plan: '',
    expiresAt: '',
    status: ApiKeyStatus.Active
  }
}

const submitForm = async () => {
  submitting.value = true
  
  try {
    if (editingApiKey.value) {
      // Update existing API key
      const updateData: UpdateApiKey = {
        plan: formData.value.plan,
        expiresAt: new Date(formData.value.expiresAt).toISOString(),
        status: formData.value.status
      }
      await adminApi.updateApiKey(editingApiKey.value.id, updateData)
    } else {
      // Create new API key
      const createData: CreateApiKey = {
        id: formData.value.id,
        plan: formData.value.plan,
        expiresAt: new Date(formData.value.expiresAt).toISOString(),
        status: formData.value.status
      }
      await adminApi.createApiKey(createData)
    }
    
    closeModal()
    await loadApiKeys()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to save API key'
  } finally {
    submitting.value = false
  }
}

const deleteApiKey = async (id: string) => {
  if (!confirm(`Are you sure you want to delete API key "${id}"?`)) {
    return
  }
  
  try {
    await adminApi.deleteApiKey(id)
    await loadApiKeys()
  } catch (err: any) {
    error.value = err.response?.data?.error || err.message || 'Failed to delete API key'
  }
}

// Search Functions
const clearSearch = () => {
  searchQuery.value = ''
}

onMounted(() => {
  loadApiKeys()
  loadPlans()
})
</script>


